"use client";

import { useTheme, alpha } from "@mui/material";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  Box,
  IconButton,
  Stack,
} from "@mui/material";
import {
  Favorite,
  FavoriteBorder,
  LocationOn,
  Work,
  School,
  AccessTime,
  CheckCircle,
} from "@mui/icons-material";
import { useWishlist } from "../../contexts/WishlistContext";

// 👇 helper function to show "-" for empty/null values
const displayValue = (value) => {
  return value && value !== "" ? value : "-";
};

function ProfileCard({
  profile,
  onViewProfile,
  onAddToWishlist,
  enquiryStatus,
  onSendEnquiry,
}) {
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const inWishlist = isInWishlist(profile.id);
  const theme = useTheme();

  const handleWishlistClick = () => {
    if (inWishlist) {
      removeFromWishlist(profile.id);
    } else {
      addToWishlist(profile);
    }
    onAddToWishlist(profile.id);
  };

  // Determine button state based on enquiry status
  let enquiryButton = (
    <Button
      variant="contained"
      fullWidth
      onClick={() => onSendEnquiry(profile.id)}
      sx={{
        flex: 1,
        borderRadius: 1.5,
        fontWeight: 500,
        fontSize: "0.85rem",
        textTransform: "none",
        height: 42,
        whiteSpace: "nowrap",
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
      }}
    >
      Enquiry
    </Button>
  );

  if (enquiryStatus === "pending") {
    enquiryButton = (
      <Button
        variant="contained"
        fullWidth
        disabled
        sx={{
          flex: 1,
          borderRadius: 1.5,
          fontWeight: 500,
          fontSize: "0.7rem", // Slightly reduced font size to fit within the button
          textTransform: "none",
          height: 42,
          whiteSpace: "wrap",
          overflow: "hidden", // Prevent overflow
          textOverflow: "ellipsis", // Add ellipsis if it still overflows
          px: 1, // Add minimal horizontal padding
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.text.disabled,
        }}
      >
        Enquiry Requested
      </Button>
    );
  } else if (enquiryStatus === "accepted") {
    // Accepted -> Show "View Contact"
    enquiryButton = (
      <Button
        variant="outlined"
        fullWidth
        onClick={() => onViewProfile(profile.user_id)}
        color="success"
        sx={{
          flex: 1,
          borderRadius: 1.5,
          fontWeight: 500,
          fontSize: "0.85rem",
          textTransform: "none",
          height: 42,
          whiteSpace: "nowrap",
          borderColor: theme.palette.success.main,
          color: theme.palette.success.main,
          "&:hover": {
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            borderColor: theme.palette.success.dark,
          },
        }}
      >
        View Contact
      </Button>
    );
  } else if (enquiryStatus === "rejected") {
    enquiryButton = (
      <Button
        variant="contained"
        fullWidth
        disabled
        sx={{
          flex: 1,
          borderRadius: 1.5,
          fontWeight: 500,
          fontSize: "0.85rem",
          textTransform: "none",
          height: 42,
          whiteSpace: "nowrap",
          backgroundColor: theme.palette.error.light,
          color: "#fff",
        }}
      >
        Rejected
      </Button>
    );
  }

  return (
    <Card
      sx={{
        height: "100%", // Allow content to dictate height, avoids stretched look
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        borderRadius: 3, // Slightly more rounded
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
        // "&:hover": {
        //   transform: "translateY(-4px)",
        //   boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
        // },
      }}
    >
      {/* Image with badges */}
      <Box sx={{ position: "relative" }}>
        <CardMedia
          component="img"
          height="220" // Reduced from 300 for better aspect ratio
          image={
            profile.profile_photo || "/placeholder.svg?height=240&width=200"
          }
          alt={`${displayValue(profile.name)}'s photo`}
          sx={{
            objectFit: "cover",
            objectPosition: "top",
            width: "100%",
          }}
        />

        {/* Icons Container - Top Right Vertical Stack */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            zIndex: 1,
          }}
        >
          {/* Verified Icon */}
          {profile.isVerified && (
            <Box
              sx={{
                width: 26,
                height: 26,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.15))",
                zIndex: 1,
              }}
              title="Verified Profile"
            >
              <svg viewBox="0 0 122.88 116.87" width="22" height="22">
                <polygon
                  fill="#10a64a"
                  points="61.37,8.24 80.43,0 90.88,17.79 111.15,22.32 109.15,42.85 122.88,58.43 109.2,73.87 111.15,94.55 91,99 80.43,116.87 61.51,108.62 42.45,116.87 32,99.08 11.73,94.55 13.73,74.01 0,58.43 13.68,42.99 11.73,22.32 31.88,17.87 42.45,0 61.37,8.24"
                />
                <path
                  fill="#ffffff"
                  d="M37.92,65c-6.07-6.53,3.25-16.26,10-10.1l8.24,7.49L74.66,39.66C81.1,33,91.27,42.78,84.91,49.48L61.67,77.2a7.13,7.13,0,0,1-9.9.44C47.83,73.89,42.05,68.5,37.92,65Z"
                />
              </svg>
            </Box>
          )}

          {/* Wishlist Icon */}
          <IconButton
            onClick={handleWishlistClick}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              width: 30,
              height: 30,
              boxShadow: 1,
              "&:hover": {
                backgroundColor: "#fff",
              },
            }}
          >
            {inWishlist ? (
              <Favorite sx={{ fontSize: 18, color: "error.main" }} />
            ) : (
              <FavoriteBorder sx={{ fontSize: 18, color: "text.secondary" }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* Card Content */}
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Stack spacing={1.2}>
          {/* Name and Age */}
          <Box>
            <Typography
              variant="h6"
              component="h3"
              sx={{ fontWeight: 600, fontSize: "1rem", mb: 0.3 }}
              noWrap
              title={profile.name}
            >
              {displayValue(profile.name)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              title={`${profile.age} years old`}
            >
              {displayValue(profile.age)} years old
            </Typography>
          </Box>

          {/* Location */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <LocationOn sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={`${profile.city}, ${profile.state}`}
            >
              {enquiryStatus === "accepted"
                ? `${displayValue(profile.city)}, ${displayValue(profile.state)}`
                : `${displayValue(profile.state)}, ${displayValue(profile.country)}`}
            </Typography>
          </Box>

          {/* Profession */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Work sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={profile.profession}
            >
              {displayValue(profile.profession)}
            </Typography>
          </Box>

          {/* Education */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <School sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={profile.education}
            >
              {displayValue(profile.education)}
            </Typography>
          </Box>

          {/* Religion */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
            <Chip
              label={displayValue(profile.religion)}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.75rem", borderRadius: "4px" }}
            />
          </Box>

          {/* Last Active */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.5 }}
          >
            <AccessTime sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={`Active ${profile.lastActive}`}
            >
              Active {displayValue(profile.lastActive)}
            </Typography>
          </Box>
        </Stack>
      </CardContent>

      {/* Action Buttons */}
      <Box sx={{ p: 2, pt: 0, mt: "auto" }}>
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
          <Button
            variant="outlined"
            onClick={() => onViewProfile(profile.user_id)}
            color="secondary"
            sx={{
              borderRadius: 1.5,
              fontWeight: 500,
              fontSize: "0.85rem",
              textTransform: "none",
              borderWidth: 1.5,
              flex: 1,
              height: 42,
              whiteSpace: "nowrap",
              "&:hover": {
                borderWidth: 1.5,
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              },
            }}
          >
            View Profile
          </Button>

          {/* Enquiry Button */}
          {enquiryButton}
        </Box>
      </Box>
    </Card>
  );
}

export default ProfileCard;
